// Classify storeTemplates docs and shape product-lineup styles.
// Product templates = blank-goods catalogs (general, nike-only, …).
// Sport artwork templates = designs + keep-logo (sport-football, …).

export const COLOR_RULES = ['black', 'grey', 'white', 'primary', 'secondary'] as const;
export type ColorRule = (typeof COLOR_RULES)[number];

export type StoreTemplateKind = 'product' | 'sport-artwork' | 'clone' | 'other';

export type TemplateLike = {
  id?: string;
  name?: string;
  description?: string;
  kind?: string;
  sport?: string;
  active?: boolean;
  ghostfit_id?: string;
  schoolId?: string;
  clonedFrom?: string;
  cloneOf?: string;
  styles?: unknown[];
};

export type ProductStyle = {
  styleNumber: string;
  colors?: Record<string, boolean>;
  brand?: string;
  category?: string;
  productTitle?: string;
  supplier?: string;
  gender?: string;
};

export type PrintPositionLike = {
  position?: string;
  display?: string;
  code?: string;
  printWidth?: number;
  printHeight?: number;
};

export type GarmentLike = {
  id?: string;
  name?: string;
  category?: string;
  supplier?: string;
  productType?: string;
  fabricComposition?: string;
  styleNumber?: string;
  styleNumbers?: Record<string, string>;
  sizes?: string[];
  tiers?: Record<string, { price?: number }>;
  printPositions?: PrintPositionLike[];
  decorationMethod?: string;
};

const CLONE_RE = /helmet pin|\bjwp\b|\bpem\b|school clone|school-specific|cloned from/i;
const LOGO_ONLY = /^(sport-)?(general|generic)(-|$)/;

const SPORT_ALIASES: Record<string, string> = {
  cheer: 'cheerleading',
  cheerleading: 'cheerleading',
  track_field: 'track',
  track_and_field: 'track',
  swim_dive: 'swimming',
  athletics: 'general',
  dance: 'cheerleading',
  spirit: 'cheerleading',
};

export function isSchoolCloneTemplate(t: TemplateLike): boolean {
  if (t.ghostfit_id || t.schoolId || t.clonedFrom || t.cloneOf) return true;
  const blob = `${t.id || ''} ${t.name || ''} ${t.description || ''}`;
  if (CLONE_RE.test(blob)) return true;
  if (/—/.test(t.name || '') && /pin|helmet|school/i.test(t.name || '')) return true;
  return false;
}

export function isSportArtworkTemplate(t: TemplateLike): boolean {
  if (t.kind === 'sport-artwork') return true;
  return String(t.id || '').startsWith('sport-');
}

export function isLogoOnlyArtwork(id: string): boolean {
  return LOGO_ONLY.test(String(id || ''));
}

export function isProductTemplate(t: TemplateLike): boolean {
  if (!t || t.active === false) return false;
  if (isSportArtworkTemplate(t)) return false;
  if (isSchoolCloneTemplate(t)) return false;
  return true;
}

export function classifyTemplate(t: TemplateLike): StoreTemplateKind {
  if (isSchoolCloneTemplate(t)) return 'clone';
  if (isSportArtworkTemplate(t)) return 'sport-artwork';
  if (t.kind === 'product' || isProductTemplate({ ...t, active: true })) return 'product';
  return 'other';
}

export function sportToArtworkTemplateId(sport: string): string | null {
  if (!sport) return null;
  const key = String(sport).toLowerCase().trim()
    .replace(/\s*\(.*\)\s*/g, '')
    .replace(/_(boys|girls|coed|mens|womens|men|women)$/, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
  if (!key) return null;
  return `sport-${SPORT_ALIASES[key] || key}`;
}

export function garmentStyleNumbers(g: GarmentLike): string[] {
  const nums = [g.styleNumber, ...(g.styleNumbers ? Object.values(g.styleNumbers) : [])];
  return [...new Set(nums.filter((s): s is string => !!s))];
}

export function styleFromGarment(g: GarmentLike, colors: Record<string, boolean>): ProductStyle {
  const styleNumber = g.styleNumber
    || g.styleNumbers?.ghostfit
    || g.styleNumbers?.sanmar
    || Object.values(g.styleNumbers || {})[0]
    || g.id
    || '';
  return {
    styleNumber,
    productTitle: g.name,
    brand: g.supplier,
    supplier: g.supplier,
    category: g.category,
    colors,
  };
}

export function brandsFromStyles(styles: ProductStyle[]): string[] {
  return [...new Set(styles.map((s) => s.brand || s.supplier).filter((b): b is string => !!b))].sort();
}

export function newProductTemplateId(name: string): string {
  const id = String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
  // Sport artwork docs live at sport-*. A product lineup must never collide.
  if (!id || id.startsWith('sport-')) return '';
  return id;
}

export function sortProductTemplates<T extends TemplateLike>(templates: T[]): T[] {
  return templates.filter(isProductTemplate).sort((a, b) => {
    if (a.id === 'general') return -1;
    if (b.id === 'general') return 1;
    return String(a.name || a.id).localeCompare(String(b.name || b.id));
  });
}

export function sortArtworkTemplates<T extends TemplateLike>(templates: T[]): T[] {
  return templates
    .filter((t) => t.active !== false && isSportArtworkTemplate(t))
    .sort((a, b) => String(a.name || a.id).localeCompare(String(b.name || b.id)));
}

function styleNumbers(styles: unknown[] | undefined): string[] {
  return (styles || []).map((s) => {
    const n = s && typeof s === 'object' && 'styleNumber' in s ? (s as { styleNumber?: unknown }).styleNumber : '';
    return String(n || '').toUpperCase();
  }).filter(Boolean);
}

export function lineupCoverage(
  productStyles: unknown[] | undefined,
  artworkStyles: unknown[] | undefined,
): { covered: number; total: number; missing: string[] } {
  const art = new Set(styleNumbers(artworkStyles));
  const prod = styleNumbers(productStyles);
  const missing = prod.filter((s) => !art.has(s));
  return { covered: prod.length - missing.length, total: prod.length, missing };
}
